import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ButtonBase,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';

import { subscribeCourse } from '../../../../store/V2/StripeSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import {
  COURSE_PRICING_TYPES,
  ICourse,
  ICoursePricing,
} from '../../../../store/V2/CoursesSlice';
import { TP_COLORS } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import { getSubjectColorNew } from '../../../../helper';
import SubscriptionLoginPopupModal from '../modals/SubscriptionLoginPopupModal';
import CourseSubscriptionOptionsModal from '../modals/CourseSubscriptionOptionsModal';

interface SubscriptionCardProps {
  course: ICourse;
}

const subscriptButtonStyles: SxProps = {
  width: '100%',
  padding: '15px',
  color: TP_COLORS.white,
  borderRadius: '5px',
  fontSize: '14px',
  fontWeight: 600,
};

const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ course }) => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);

  // State
  const [isLoginPopupModalOpen, setLoginPopupModalOpen] = useState(false);
  const [isSubscribeOptionModalOpen, setSubscribeOptionModalOpen] = useState(
    false
  );
  const [isLoading, setLoading] = useState(false);

  const authUser = authState.user;
  const subjectColor = course.subject
    ? getSubjectColorNew(course.subject)
    : '#1C9FF7';

  const pricings = course.pricings || [];
  const isGuest =
    !authUser || (authUser && authUser.role && authUser.role.isGuest);

  const toggleLoginPopupModal = () => {
    setLoginPopupModalOpen((prevState) => !prevState);
  };

  const toggleSubscribeOptionModal = () => {
    setSubscribeOptionModalOpen((prevState) => !prevState);
  };

  const enrollToCourse = async (option: ICoursePricing) => {
    setLoading(true);

    await dispatch(
      subscribeCourse({
        deviceTimezone,
        courseId: course._id,
        subscriptionType: option.type,
      })
    );

    setLoading(false);
  };

  const handleSubscribe = async () => {
    if (isGuest) {
      toggleLoginPopupModal();
    } else if (pricings.length > 1) {
      toggleSubscribeOptionModal();
    } else if (pricings.length === 1) {
      const selectedPaymentOption = pricings[0];

      enrollToCourse(selectedPaymentOption);
    }
  };

  const CustomListItem = ({ text }: { text: string }) => {
    return (
      <Typography
        component="li"
        color="#003768"
        fontSize="13px"
        fontWeight={600}
        lineHeight="15px"
        sx={{ mb: '20px', '&::marker': { color: subjectColor } }}
      >
        {text}
      </Typography>
    );
  };

  const renderPricings = () => {
    const pricingElements: JSX.Element[] = [];

    const renderOrDivider = (key: string) => (
      <Typography
        key={key}
        fontSize="16px"
        fontWeight={400}
        textAlign="center"
        color="#647488"
      >
        or
      </Typography>
    );

    pricings.forEach((item, index) => {
      const hasOnlyOnePricingOption = pricings.length === 1;

      if (item.type === COURSE_PRICING_TYPES.CREDIT) {
        pricingElements.push(
          <div key={`price-option-${COURSE_PRICING_TYPES.CREDIT}-${index}`}>
            <Typography
              fontSize="20px"
              textAlign="center"
              fontWeight={600}
              color={subjectColor}
            >
              Enroll from your credits
            </Typography>
          </div>,
          renderOrDivider(`price-option-${index}`)
        );
      }

      if (
        item.type === COURSE_PRICING_TYPES.ONETIME &&
        item.amount &&
        item.currency
      ) {
        pricingElements.push(
          <div key={`price-option-${COURSE_PRICING_TYPES.ONETIME}-${index}`}>
            <Typography
              fontSize={hasOnlyOnePricingOption ? '44px' : '20px'}
              fontWeight={900}
              textAlign="center"
              color={subjectColor}
            >
              {item.currency} {item.amount.toFixed(2)}
            </Typography>
          </div>,
          renderOrDivider(`price-option-${index}`)
        );
      }

      if (
        item.type === COURSE_PRICING_TYPES.SUBSCRIPTION &&
        item.amount &&
        item.currency &&
        item.billingPeriod
      ) {
        pricingElements.push(
          <div
            key={`price-option-${COURSE_PRICING_TYPES.SUBSCRIPTION}-${index}`}
          >
            <div>
              <Typography
                fontSize={hasOnlyOnePricingOption ? '30px' : '20px'}
                fontWeight={900}
                textAlign="center"
                color={subjectColor}
              >
                {item.currency} {item.amount.toFixed(2)} per{' '}
                {item.billingPeriod.count > 1
                  ? `${item.billingPeriod.count} `
                  : null}
                {item.billingPeriod.type.toLowerCase().slice(0, -1)}
                {item.billingPeriod.count > 1 ? 's' : ''}
              </Typography>
            </div>
          </div>,
          renderOrDivider(`price-option-${index}`)
        );
      }
    });

    pricingElements.splice(-1);

    return pricingElements;
  };

  return (
    <Card
      elevation={0}
      style={{
        backgroundColor: '#F1F5F9',
        borderRadius: '20px',
        maxWidth: '303px',
      }}
    >
      <CardContent style={{ padding: '10px' }}>
        <div style={{ padding: '20px' }}>
          <Typography
            fontSize="16px"
            fontWeight={500}
            color="#1E293B"
            textAlign="center"
            lineHeight="19px"
          >
            {course.title}
          </Typography>
        </div>
        {renderPricings()}
        <Stack alignItems="center" style={{ padding: '20px 15px' }}>
          <ButtonBase
            onClick={handleSubscribe}
            disabled={isLoading || !pricings.length}
            sx={{ ...subscriptButtonStyles, backgroundColor: subjectColor }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={15} sx={{ color: '#ffffff' }} />
                &nbsp;
              </>
            ) : (
              'ENROLL'
            )}
          </ButtonBase>
        </Stack>
        <div style={{ padding: '0 25px' }}>
          <Typography component="ul" style={{ margin: 0, padding: 0 }}>
            {(course.pricingDetails || []).map((item, index) => (
              <CustomListItem key={`li-${index}`} text={item} />
            ))}
          </Typography>
        </div>
        {/* <Card
          sx={{
            background: `url('${process.env.PUBLIC_URL}/images/shapes/idk-teacher.png') bottom right/contain no-repeat, linear-gradient(103.87deg, #006C9C 7.37%, #002536 100%)`,
          }}
        >
          <CardContent style={{ paddingBottom: '16px' }}>
            <div style={{ marginBottom: '20px' }}>
              <Typography
                fontSize="20px"
                fontWeight={700}
                color={TP_COLORS.white}
              >
                Still Deciding?
              </Typography>
            </div>
            <div>
              <Button size="small" variant="contained" color="inherit">
                Book A Trial Class
              </Button>
            </div>
          </CardContent>
        </Card> */}
      </CardContent>
      {/* Modals */}
      <SubscriptionLoginPopupModal
        open={isLoginPopupModalOpen}
        onClose={toggleLoginPopupModal}
      />
      {isSubscribeOptionModalOpen ? (
        <CourseSubscriptionOptionsModal
          open={isSubscribeOptionModalOpen}
          onClose={toggleSubscribeOptionModal}
          pricings={pricings}
          onSubmit={enrollToCourse}
        />
      ) : null}
    </Card>
  );
};

export default SubscriptionCard;
